import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BatteryCharging, Calendar, CloudSun, Droplets, Fan, Flame, Home, PlugZap, Sun, Thermometer, Zap } from 'lucide-react';
import { MODALITA_SISTEMA, type ModalitaSistema } from '@emacomfort/domain';
import { useComando, useObsoleto, useStato } from '@/app/DeviceProvider';
import { useDevice } from '@/app/DeviceProvider';
import { useFormato } from '@/app/formato';
import { Avviso, Badge, Bottone, Card, Segmenti, StatoComandoVista, Stepper, Valore, cx, stili } from '@/ui';
import { useAttuatore, useCanalePerFunzione, useComandabile, useMediaAmbiente, useValoreDifferito, useValoreFunzione, useZone } from '../comune';
import { Sinottico } from './Sinottico';
import s from '../features.module.css';

function Lettura({ etichetta, valore, obsoleto, q, dimensione = 'secondario', nota }: { etichetta: string; valore: string; obsoleto?: boolean; q?: string; dimensione?: 'primario' | 'secondario' | 'terziario'; nota?: string }) {
  const { t } = useTranslation();
  return (
    <div className={s.lettura}>
      <span className={stili.etichetta}>{etichetta}</span>
      <Valore numero={valore} dimensione={dimensione} obsoleto={obsoleto} />
      {q === 'sonda_scollegata' ? <span className={s.letturaNota}>{t('stato.sondaScollegata')}</span> : nota ? <span className={s.letturaNota}>{nota}</span> : null}
    </div>
  );
}

function CardAmbiente() {
  const { t } = useTranslation();
  const f = useFormato();
  const { invia } = useDevice();
  const media = useMediaAmbiente();
  const { obsoleto } = useObsoleto();
  const umidita = useValoreFunzione('UMIDITA_INTERNA');
  const esterna = useValoreFunzione('TEMPERATURA_ESTERNA');
  const zone = useZone();
  const comandoGlobale = useComando('globale');
  const comandabile = useComandabile();
  const setpointGlobale = zone.filter((z) => !z.sovrascritta)[0]?.setpoint ?? zone[0]?.setpoint ?? 20;
  const globale = useValoreDifferito(setpointGlobale, (v) => invia({ azione: 'set_setpoint_globale', valore: v }));

  return (
    <Card titolo={t('pannello.ambiente')} icona={<Home size={20} aria-hidden />}>
      <div className={s.letturaRiga}>
        <Lettura etichetta={t('pannello.mediaAmbiente')} valore={f.temp(media)} obsoleto={obsoleto} dimensione="primario" />
        {umidita.canale && <Lettura etichetta={t('pannello.umidita')} valore={umidita.valore === null ? '— —' : f.pct(umidita.valore)} obsoleto={umidita.obsoleto} q={umidita.q} />}
      </div>
      {/* Su un impianto in sola lettura il passo del setpoint non e' nascosto
          per prudenza: non esiste proprio nulla da comandare, e offrire un
          comando che puo' solo essere rifiutato e' peggio che non offrirlo
          (ADR-060). Al suo posto, il perche'. */}
      {comandabile ? (
        <div className={s.impostazione}>
          <div>
            <div className={stili.etichetta}>{t('zone.globale')}</div>
            <StatoComandoVista esito={comandoGlobale?.esito} motivo={comandoGlobale?.motivo} />
          </div>
          <Stepper
            valore={f.tempNumero(globale.valore)}
            onMeno={() => globale.imposta(globale.valore - 0.5)}
            onPiu={() => globale.imposta(globale.valore + 0.5)}
            etichettaMeno={t('zone.meno', { zona: t('zone.globale') })}
            etichettaPiu={t('zone.piu', { zona: t('zone.globale') })}
            disabilitato={obsoleto}
          />
        </div>
      ) : (
        <Avviso tono="info">{t('pannello.solaLettura')}</Avviso>
      )}
      {esterna.canale && (
        <div className={s.letturaRiga}>
          <Lettura etichetta={t('pannello.esterna')} valore={f.temp(esterna.valore)} obsoleto={esterna.obsoleto} q={esterna.q} />
          <CloudSun size={40} aria-hidden className={stili.muto} />
        </div>
      )}
    </Card>
  );
}

function CardCentrale() {
  const { t } = useTranslation();
  const f = useFormato();
  const mandata = useValoreFunzione('MANDATA_COLLETTORE');
  const ritorno = useValoreFunzione('RITORNO_COLLETTORE');
  const acs = useValoreFunzione('ACCUMULO_SANITARIO');
  const pufferAlto = useValoreFunzione('PUFFER_ALTO');
  const deltaT = mandata.valore !== null && ritorno.valore !== null ? mandata.valore - ritorno.valore : null;
  // Un impianto letto da un connettore di terze parti non ha sonde di centrale
  // termica: mandata, ritorno e accumulo non esistono come dato. Quattro
  // trattini senza spiegazione si leggono come un guasto; qui si dice invece
  // che quel dato questa sorgente non lo pubblica.
  if (!mandata.canale && !ritorno.canale && !acs.canale && !pufferAlto.canale) {
    return (
      <Card titolo={t('pannello.centraleTermica')} icona={<Thermometer size={20} aria-hidden />}>
        <p className={s.letturaNota}>{t('pannello.centraleSenzaSonde')}</p>
      </Card>
    );
  }
  return (
    <Card titolo={t('pannello.centraleTermica')} icona={<Thermometer size={20} aria-hidden />}>
      <div className={s.letture}>
        {mandata.canale && <Lettura etichetta={t('pannello.mandata')} valore={f.temp(mandata.valore)} obsoleto={mandata.obsoleto} q={mandata.q} />}
        {ritorno.canale && <Lettura etichetta={t('pannello.ritorno')} valore={f.temp(ritorno.valore)} obsoleto={ritorno.obsoleto} q={ritorno.q} nota={`${t('pannello.deltaT')}: ${f.delta(deltaT)}`} />}
        {pufferAlto.canale ? (
          <Lettura etichetta={t('pannello.accumulo')} valore={f.temp(pufferAlto.valore)} obsoleto={pufferAlto.obsoleto} q={pufferAlto.q} />
        ) : (
          <Lettura etichetta={t('pannello.accumulo')} valore="— —" nota={t('pannello.accumuloSenzaSonda')} />
        )}
        {acs.canale && <Lettura etichetta={t('pannello.sanitario')} valore={f.temp(acs.valore)} obsoleto={acs.obsoleto} q={acs.q} />}
      </div>
    </Card>
  );
}

function CardModalita() {
  const { t } = useTranslation();
  const { invia } = useDevice();
  const modalita = useStato((st) => st.stato?.modalita ?? st.impianto?.modalita ?? null);
  const comando = useComando('modalita');
  const { obsoleto } = useObsoleto();
  const comandabile = useComandabile();
  const richiesta = comando && comando.esito !== 'fallito' && comando.azione.azione === 'set_modalita' ? comando.azione.modalita : null;
  const icone: Record<ModalitaSistema, React.ReactNode> = {
    RISCALDAMENTO: <Flame size={22} aria-hidden />,
    RAFFRESCAMENTO: <Fan size={22} aria-hidden />,
    AUTOMATICO: <Sun size={22} aria-hidden />,
    SPENTO: <PlugZap size={22} aria-hidden />,
  };
  return (
    <Card titolo={t('modalita.titolo')} icona={<Sun size={20} aria-hidden />}>
      <Segmenti
          colonne={2}
          etichetta={t('modalita.titolo')}
          valore={richiesta ?? modalita}
          onChange={(m) => void invia({ azione: 'set_modalita', modalita: m })}
          opzioni={MODALITA_SISTEMA.map((m) => ({ valore: m, etichetta: t(`modalita.${m}`), icona: icone[m] }))}
          disabilitato={obsoleto || !comandabile}
        />
      {/* La modalita' resta VISIBILE anche in sola lettura — e' un dato che la
          sorgente pubblica — ma non selezionabile: cambiarla e' un comando. */}
      {comandabile ? (
        <StatoComandoVista esito={comando?.esito} motivo={comando?.motivo} />
      ) : (
        <p className={s.letturaNota}>{t('pannello.solaLettura')}</p>
      )}
      <Bottone variante="fantasma" pieno>
        <Link to="/programmazione" className={stili.riga}>
          <Calendar size={22} aria-hidden />
          {t('pannello.programmazioneOraria')}
        </Link>
      </Bottone>
    </Card>
  );
}

function CardEnergia() {
  const { t } = useTranslation();
  const f = useFormato();
  const impianto = useStato((st) => st.impianto);
  const adesso = useStato((st) => st.adesso);
  const sorgenteId = impianto?.energia.sorgenteId;
  const energia = useStato((st) => (sorgenteId ? st.energia[sorgenteId] : undefined));
  const sorgente = useStato((st) => (sorgenteId ? st.sorgenti[sorgenteId] : undefined));
  const pdc = impianto?.generatori.find((g) => g.tipo === 'POMPA_DI_CALORE');
  const comandoPdc = useCanalePerFunzione('COMANDO_GENERATORE', pdc?.id);
  const sorgentePdc = impianto?.moduli.find((m) => m.id === comandoPdc?.moduloId)?.sorgenteId;
  const pdcComandabile = impianto?.sorgenti.find((x) => x.id === sorgentePdc)?.comandabile ?? (impianto?.centralina.tipo === 'emacomfort');
  if (!impianto?.energia.presente) return null;
  const giu = !sorgente?.raggiungibile;
  const surplus = energia ? energia.produzioneW - energia.consumoW : 0;
  const haBatteria = (impianto.energia.capacitaBatteriaKwh ?? 0) > 0;
  return (
    <>
      <Card
        titolo={t('pannello.energia.titolo')}
        icona={<Zap size={20} aria-hidden />}
        azione={giu ? <Badge tono="warn">{t('stato.ultimoDato', { quando: f.relativo(sorgente?.ultimoAggiornamento, adesso) })}</Badge> : <Badge tono="ok">{t('stato.aggiornatoOra')}</Badge>}
      >
        <div className={s.energiaGriglia}>
          <div className={s.energiaVoce}>
            <span className={stili.etichetta}>{t('pannello.energia.produzione')}</span>
            <Valore numero={giu ? '— —' : f.kw(energia?.produzioneW)} dimensione="secondario" obsoleto={giu} />
          </div>
          <div className={s.energiaVoce}>
            <span className={stili.etichetta}>{t('pannello.energia.consumo')}</span>
            <Valore numero={giu ? '— —' : f.kw(energia?.consumoW)} dimensione="secondario" obsoleto={giu} />
          </div>
          <div className={s.energiaVoce}>
            <span className={stili.etichetta}>{t('pannello.energia.rete')}</span>
            <Valore numero={giu ? '— —' : f.kw(energia?.reteW)} dimensione="secondario" obsoleto={giu} />
            {!giu && energia && <span className={s.letturaNota}>{energia.reteW >= 0 ? t('pannello.energia.prelievo') : t('pannello.energia.immissione')}</span>}
          </div>
          {haBatteria && (
            <div className={s.energiaVoce}>
              <span className={stili.etichetta}>{t('pannello.energia.batteria')}</span>
              <Valore numero={giu ? '— —' : f.pct(energia?.batteriaPct)} dimensione="secondario" obsoleto={giu} />
              <div className={s.barra} aria-hidden>
                <div className={s.barraRiempimento} style={{ width: `${giu ? 0 : (energia?.batteriaPct ?? 0)}%` }} />
              </div>
              {!giu && energia && energia.batteriaW !== 0 && <span className={s.letturaNota}>{energia.batteriaW > 0 ? t('pannello.energia.carica') : t('pannello.energia.scarica')}</span>}
            </div>
          )}
        </div>
      </Card>
      <Card titolo={t('pannello.energia.surplusTitolo')} icona={<BatteryCharging size={20} aria-hidden />} azione={!pdcComandabile && <Badge tono="neutro">{t('pannello.energia.fase2')}</Badge>}>
        {giu ? (
          <Avviso tono="warn">{t('stato.sorgenteCaduta')}</Avviso>
        ) : surplus > 300 ? (
          <>
            <Valore numero={f.kw(surplus)} dimensione="secondario" />
            <p>{t('pannello.energia.surplusDisponibile', { kw: f.numero(surplus / 1000, 1) })}</p>
            <p className={stili.muto}>{t('pannello.energia.versoPdc')}</p>
          </>
        ) : (
          <p>{t('pannello.energia.surplusNessuno')}</p>
        )}
        <Avviso tono={pdcComandabile ? 'ok' : 'info'}>{pdcComandabile ? t('pannello.energia.comandoDisponibile') : t('pannello.energia.comandoNonDisponibile')}</Avviso>
      </Card>
    </>
  );
}

function CardSorgenti() {
  const { t } = useTranslation();
  const f = useFormato();
  const impianto = useStato((st) => st.impianto);
  const sorgenti = useStato((st) => st.sorgenti);
  const adesso = useStato((st) => st.adesso);
  // La soglia era "almeno due sorgenti", perche' con una sola sorgente propria
  // la scheda non aggiunge nulla. Ma un impianto letto da un connettore ha
  // esattamente UNA sorgente, ed e' questa scheda a dichiararne la sola lettura
  // e la freschezza: nasconderla toglieva proprio il caso che serve vedere.
  const soloLettura = impianto?.sorgenti.some((so) => !so.comandabile) ?? false;
  if (!impianto || (impianto.sorgenti.length < 2 && !soloLettura)) return null;
  return (
    <Card titolo={t('pannello.sorgenti')} icona={<PlugZap size={20} aria-hidden />}>
      <div className={s.sorgenti}>
        {impianto.sorgenti.map((so) => {
          const st = sorgenti[so.id];
          const giu = !st?.raggiungibile;
          return (
            <div key={so.id} className={cx(s.sorgente, giu && s.sorgenteGiu)}>
              <div className={stili.riga}>
                <span className={s.sorgenteNome}>{so.nome}</span>
                <span className={stili.spazio} />
                <Badge tono={giu ? 'warn' : 'ok'}>{giu ? t('stato.sorgenteCaduta') : t('stato.connesso')}</Badge>
              </div>
              <span className={s.letturaNota}>
                {t(`sorgenti.tipo.${so.tipo}`)} · {so.comandabile ? t('sorgenti.comandabile') : t('sorgenti.solaLettura')}
              </span>
              <span className={s.letturaNota}>{giu ? t('stato.ultimoDato', { quando: f.relativo(st?.ultimoAggiornamento, adesso) }) : t('stato.aggiornatoAlle', { ora: f.ora(st?.ultimoAggiornamento) })}</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function CardServizi() {
  const { t } = useTranslation();
  const f = useFormato();
  const impianto = useStato((st) => st.impianto);
  const sanitario = useStato((st) => st.stato?.sanitario);
  const vmc = useStato((st) => st.stato?.vmc);
  const acs = useValoreFunzione('ACCUMULO_SANITARIO');
  const vmcCanale = useCanalePerFunzione('VMC');
  const vmcAttuatore = useAttuatore(vmcCanale?.id);
  if (!impianto) return null;
  const voci: React.ReactNode[] = [];
  if (impianto.sanitario.presente) {
    voci.push(
      <Link key="acs" to="/sanitario" className={cx(stili.card, stili.riga)} style={{ textDecoration: 'none' }}>
        <Droplets size={28} aria-hidden className={stili.muto} />
        <div className={s.lettura}>
          <span className={stili.etichetta}>{t('sanitario.titolo')}</span>
          {/* Hive non pubblica alcuna temperatura per l'acqua calda: e' un
              on/off. Mostrare "- -" farebbe sembrare rotta una sonda che non
              esiste (ADR-060). */}
          {sanitario?.soloOnOff ? (
            <span className={stili.valoreTerziario}>{sanitario.inCarica ? t('sanitario.accesa') : t('sanitario.spenta')}</span>
          ) : (
            <Valore numero={f.temp(acs.valore)} dimensione="terziario" obsoleto={acs.obsoleto} />
          )}
        </div>
        <span className={stili.spazio} />
        {sanitario?.soloOnOff ? (
          <Badge tono="neutro">{t('sorgenti.solaLettura')}</Badge>
        ) : (
          <Badge tono={sanitario?.inCarica ? 'info' : 'ok'}>{sanitario?.inCarica ? t('sanitario.inCarica') : t('sanitario.aRegime')}</Badge>
        )}
      </Link>,
    );
  }
  if (impianto.vmc.presente) {
    const attiva = vmcAttuatore?.stato === 'attivo';
    voci.push(
      <Link key="vmc" to="/ventilazione" className={cx(stili.card, stili.riga)} style={{ textDecoration: 'none' }}>
        <Fan size={28} aria-hidden className={stili.muto} />
        <div className={s.lettura}>
          <span className={stili.etichetta}>{t('vmc.titolo')}</span>
          <span className={stili.valoreTerziario}>{t(`vmc.modalita.${vmc?.modalita ?? impianto.vmc.modalita}`)}</span>
        </div>
        <span className={stili.spazio} />
        <Badge tono={attiva ? 'ok' : 'neutro'}>{attiva ? t('vmc.attiva') : t('vmc.inattiva')}</Badge>
      </Link>,
    );
  }
  if (voci.length === 0) return null;
  return <div className={s.grigliaDue}>{voci}</div>;
}

export function PannelloPage() {
  const { t } = useTranslation();
  const pronto = useStato((st) => st.pronto);
  const impianto = useStato((st) => st.impianto);
  if (!pronto || !impianto) return <div className={stili.pagina}>{t('app.caricamento')}</div>;
  return (
    <div className={stili.pagina}>
      <h1 className="visually-hidden">{t('pannello.titolo')}</h1>
      <Card className={s.sinotticoCard}>
        <Sinottico />
      </Card>
      <div className={s.grigliaPannello}>
        <CardAmbiente />
        <CardCentrale />
        <CardModalita />
      </div>
      {impianto.energia.presente && (
        <div className={s.grigliaDue}>
          <CardEnergia />
        </div>
      )}
      <CardServizi />
      <CardSorgenti />
    </div>
  );
}
